/**
 * @fileoverview Audit Log table loading placeholder.
 * @description Pulsing placeholder rows shown inside the Audit Log card while the first page of
 * events loads, so the filter bar above stays put and usable. Matches the real table's column
 * count so the layout doesn't jump when the rows arrive.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import Skeleton from '@/shared/components/atoms/Skeleton'

interface AuditTableSkeletonProps {
  columns?: number
  rows?: number
}

/**
 * @component
 * @param {AuditTableSkeletonProps} props - How many placeholder columns and rows to render.
 * @param {number} [props.columns] - Column count. Defaults to 6.
 * @param {number} [props.rows] - Row count. Defaults to 8.
 */
function AuditTableSkeleton({ columns = 6, rows = 8 }: AuditTableSkeletonProps) {
  return (
    <div role="status" aria-busy="true" data-testid="audit-table-skeleton">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div
          key={rowIndex}
          className="grid items-center gap-3 border-b border-row-border px-4 py-3.5 last:border-b-0 sm:px-5"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: columns }, (_, colIndex) => (
            <Skeleton key={colIndex} width={colIndex === 2 ? '80%' : '60%'} height={13} />
          ))}
        </div>
      ))}
    </div>
  )
}

export default AuditTableSkeleton
